import React from 'react';
import universal from 'react-universal-component';
import { Route, Switch, Redirect } from 'react-router-dom';
import Box from '@mui/material/Box';
import { useSelector } from 'react-redux';
import Spin from 'components/Spin';
import AppHeader from 'components/AppHeader';
import SideNav from './components/Navigation';
import AuthWrapper from 'components/AuthWrapper';
import SuspendedBanner from 'components/SuspendedBanner';
import SyncStatusBanner from 'modules/settings/components/SyncStatusBanner';
import KickStart from 'modules/kickstart/Kickstart';
import ViewForm from 'modules/forms/containers/ViewForm';
import Videos from 'modules/videos';
import ConnectCard from 'modules/connect-card';
import Academy from './containers/academy';
import DialerWidget from 'modules/dialer-widget/Widget';
import SenderProfileStatus from './components/SenderProfileStatus';
import FEATURES from './constants/features';
import WithFeature, { defaultErrorMessage } from './components/WithFeature';
import { STAGE } from 'constants/config';
import { MODULES, ACTIONS, ProtectedRoute } from 'components/Access';
import OauthLogin from 'containers/oauthLogin';
import NotificationListener from 'modules/notifications/components/NotificationListener';
import StripeSetupRedirect from './containers/StripeSetupRedirect';
import Import from 'modules/settings/containers/Import';
import Export from 'modules/settings/containers/Export';

const Loading = () => <Spin spinning className="mt-3" />;

const Error = (props) => {
  console.log(props.error.stack);
  return (
    <div className="p-4">
      <h5>This is taking too long</h5>
      <p>
        We're experiencing increased traffic. Please try{' '}
        <a href="">reloading</a> the page or try after sometime.
      </p>
      <p>{props.error.message}</p>
    </div>
  );
};

export const CheckoutModule = universal(() => import('modules/public/app'), {
  loading: Loading,
  error: Error,
  timeout: 30000,
  ignoreBabelRename: true,
});

const Dashboard = universal(
  () => import('modules/dashboard/containers/Dashboard'),
  {
    loading: Loading,
    error: Error,
    ignoreBabelRename: true,
    timeout: 30000,
  },
);

const MessagesModule = universal(() => import('modules/messages'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});
const PeopleModule = universal(() => import('modules/people'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const GroupModule = universal(() => import('modules/groups'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const SettingsModule = universal(() => import('modules/settings'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
  timeout: 30000,
});

export const OnboardingModule = universal(import('modules/onboarding'), {
  loading: Loading,
  error: Error,
  // ignoreBabelRename: true,
  timeout: 30000,
});

export const PLOnboardingModule = universal(
  import('modules/PastorsLineOnboarding'),
  {
    loading: Loading,
    error: Error,
    timeout: 30000,
  },
);

export const AdminModule = universal(() => import('modules/admin'), {
  loading: Loading,
  error: Error,
  // ignoreBabelRename: true,
});

const Notifications = universal(() => import('modules/notifications'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const PhoneAppModule = universal(() => import('modules/phoneapp'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const WorkflowsModule = universal(() => import('modules/automated-workflows'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const QRModule = universal(() => import('modules/qr-code'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const URLShortenerModule = universal(() => import('modules/url-shortener'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const NotesModule = universal(() => import('modules/notes'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const MarketplaceModule = universal(() => import('modules/marketplace'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const TaskModule = universal(() => import('modules/tasks'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const FilesModule = universal(() => import('modules/files'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

const PeopleReportsModule = universal(() => import('modules/peopleReports'), {
  loading: Loading,
  error: Error,
  ignoreBabelRename: true,
});

export const AppLayout = () => {
  const syncIntegration = useSelector(
    (s) => s.settings.integrations.syncIntegration,
  );

  return (
    <React.Fragment>
      {syncIntegration && <SyncStatusBanner />}

      <Box display="flex">
        <SideNav />
        <Box
          height="100vh"
          overflow="hidden"
          flex={1}
          display="flex"
          flexDirection="column"
        >
          <SuspendedBanner />
          <SenderProfileStatus />
          <AppHeader />
          <AuthWrapper>
            <WithFeature
              feature={FEATURES.TALK_DIGITAL_RECEPTION}
              message={false}
            >
              <DialerWidget />
            </WithFeature>

            <NotificationListener />
            <Switch>
              <Route exact path="/" component={Dashboard} />
              <ProtectedRoute
                module={MODULES.MESSAGES}
                path="/messages"
                component={MessagesModule}
              />
              <ProtectedRoute
                module={MODULES.CONTACTS}
                path="/people"
                component={PeopleModule}
              />
              <ProtectedRoute
                features={[FEATURES.GROUP]}
                module={MODULES.GROUPS}
                path="/groups"
                component={GroupModule}
              />
              <Route path="/settings" component={SettingsModule} />
              <Route path="/notifications" component={Notifications} />
              <Route path="/igniterkit" component={KickStart} />
              <Route path="/kickstart_easter">
                <KickStart type="easter" />
              </Route>
              <ProtectedRoute
                module={MODULES.VIDEO_NOTES}
                path="/pages"
                component={Videos}
              />
              <ProtectedRoute
                features={[FEATURES.CONNECT_CARD]}
                module={MODULES.CONNECT_CARDS}
                path="/connect"
                component={ConnectCard}
              />
              <Route path="/academy" component={Academy} />
              <Route path="/calling" component={PhoneAppModule} />
              <ProtectedRoute
                features={FEATURES.WORKFLOWS}
                module={MODULES.WORKFLOWS}
                path="/workflows"
                component={WorkflowsModule}
              />
              <Route path="/qr-code" component={QRModule} />
              <Route path="/url-shortener" component={URLShortenerModule} />
              <Route path="/import-contacts" component={Import} />
              <Route path="/export-contacts" component={Export} />
              <Route path="/files" component={FilesModule} />
              <Route path="/reports" component={PeopleReportsModule} />
              <ProtectedRoute
                module={MODULES.VIDEO_NOTES}
                path="/notes"
                component={NotesModule}
              />
              <ProtectedRoute
                module={MODULES.BILLING}
                action={ACTIONS.MODIFY}
                path="/marketplace"
                component={MarketplaceModule}
              />
              <Route path="/oauthLogin" component={OauthLogin} />
              <Route path="/forms/:id" component={ViewForm} />
              <Route path="/stripe-setup" component={StripeSetupRedirect} />
              <Route path="/tasks" component={TaskModule} />
              <Redirect to="/" />
            </Switch>
          </AuthWrapper>
        </Box>
      </Box>
    </React.Fragment>
  );
};

export const Admin = AdminModule;
